import { useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Check, Copy, ArrowRight, ArrowLeft, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useMe } from '@/hooks/useMe';
import { AuthLayout } from './LoginPage';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const STEPS = ['Company', 'Domain', 'Install'];
const FUNNEL_OPTIONS = [
  { value: 'webinar', label: 'Webinar', desc: 'Registration → live/replay → offer' },
  { value: 'vsl_long', label: 'VSL · Long nurture', desc: 'Video sales letter, longer email nurture' },
  { value: 'vsl_short', label: 'VSL · Short nurture', desc: 'Video sales letter, fast follow-up' },
  { value: 'ecom', label: 'E-commerce', desc: 'Product store, cart and checkout' },
  { value: 'standard', label: 'Standard', desc: 'Landing page → checkout' },
];

function Mono({ children, label, value }) {
  const [status, setStatus] = useState('idle');
  const copying = useRef(false);
  const handleCopy = async () => {
    if (copying.current || !value) return;
    copying.current = true;
    setStatus('copying');
    try {
      await navigator.clipboard.writeText(value);
      setStatus('copied');
      toast.success(`${label} copied`);
    } catch {
      setStatus('error');
      toast.error('Could not copy. Select the text and copy it manually.');
    } finally {
      copying.current = false;
    }
  };
  return <div>
    <div className="sp-auth-code">{value ? <code>{children}</code> : <span>Unavailable — refresh workspace details.</span>}
      <button type="button" className="sp-secondary-button" onClick={handleCopy} disabled={!value || status === 'copying'} aria-label={`Copy ${label}`} aria-busy={status === 'copying'}>
        {status === 'copied' ? <Check size={15} aria-hidden="true" /> : <Copy size={15} aria-hidden="true" />}
        <span aria-live="polite">{status === 'copying' ? 'Copying…' : status === 'copied' ? 'Copied' : 'Copy'}</span>
      </button>
    </div>
    {status === 'error' && <div className="sp-auth-notice" data-tone="error" role="alert">Could not copy. Select the text and copy it manually.</div>}
  </div>;
}

function Field({ label, id, hint, children }) {
  return <div className="sp-auth-field">
    {id ? <label htmlFor={id}>{label}</label> : <span>{label}</span>}
    {children}
    {hint && <small id={id ? `${id}-hint` : undefined}>{hint}</small>}
  </div>;
}

export default function OnboardingFlow() {
  const qc = useQueryClient();
  const me = useMe();
  const [step, setStep] = useState(0);
  const [company, setCompany] = useState(me?.name && me.name !== 'Workspace' ? me.name : '');
  const [funnel, setFunnel] = useState(me?.funnel_type || '');
  const [domain, setDomain] = useState(me?.tracking_domain || '');
  const [saving, setSaving] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const submitting = useRef(false);
  const refreshingProfile = useRef(false);
  const [verifyResult, setVerifyResult] = useState(() => me?.domain_verified && me?.tracking_domain
    ? { verified: true, domain: me.tracking_domain } : null);

  const cnameTarget = me?.cname_target || 'app.tethertracking.com';
  const domainSub = domain.trim() ? domain.trim().replace(/^https?:\/\//, '').split('/')[0] : 'track.yourdomain.com';
  const sysHost = me?.system_tracking_host;
  const domainLive = verifyResult?.verified === true && verifyResult.domain === domain.trim();
  const trackingBase = domainLive ? `https://${verifyResult.domain}` : (sysHost ? `https://${sysHost}` : BACKEND_URL);
  const snippet = me?.site_key ? `<script src="${trackingBase}/api/page.js?k=${me.site_key}"></script>` : '';
  const webhook = me?.api_key ? `${trackingBase}/api/sales/webhook?key=${me.api_key}` : '';
  const refreshProfile = async () => {
    if (refreshingProfile.current) return;
    refreshingProfile.current = true;
    setRefreshing(true);
    try {
      await qc.invalidateQueries({ queryKey: ['me'] });
    } catch {
      setError('Could not refresh workspace details. Try again.');
    } finally {
      refreshingProfile.current = false;
      setRefreshing(false);
    }
  };

  const save = async (body, afterSave) => {
    if (submitting.current) return false;
    submitting.current = true;
    setSaving(true);
    setError('');
    try {
      const res = await fetch(`${BACKEND_URL}/api/tenant/settings`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Could not save your workspace. Try again.');
      if (afterSave) await afterSave(data);
      await qc.invalidateQueries({ queryKey: ['me'] });
      return true;
    } catch (e) {
      const message = e.message || 'Could not save your workspace. Try again.';
      setError(message);
      toast.error(message);
      return false;
    } finally {
      submitting.current = false;
      setSaving(false);
      setVerifying(false);
    }
  };

  const nextFromCompany = async e => {
    e.preventDefault();
    if (submitting.current) return;
    if (!company.trim()) { setError('Enter your company name'); return; }
    if (!funnel) { setError('Choose your funnel type'); return; }
    if (await save({ name: company.trim(), funnel_type: funnel })) setStep(1);
  };
  const nextFromDomain = async skip => {
    if (submitting.current) return;
    if (skip || !domain.trim()) { setError(''); setStep(2); return; }
    setVerifyResult(null);
    const saved = await save({ tracking_domain: domain.trim() }, async () => {
      setVerifying(true);
      const res = await fetch(`${BACKEND_URL}/api/tenant/tracking-domain/verify`, { method: 'POST' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Domain saved, but verification failed. Try again or skip for now.');
      setVerifyResult(data);
      if (data.verified) toast.success('Domain verified — tracking is live on it');
      else toast.info('Domain saved — verify it from Settings once DNS propagates');
    });
    if (saved) setStep(2);
  };
  const finish = async () => {
    await save({ onboarded: true }, async data => {
      let stored = {};
      try { stored = JSON.parse(localStorage.getItem('tether_me') || '{}') || {}; } catch { /* A corrupt cache must not undo a confirmed server save. */ }
      const completed = { ...data, onboarded: true, name: data.name || company.trim() || stored.name };
      try {
        localStorage.setItem('tether_me', JSON.stringify({ ...stored, onboarded: true, name: completed.name }));
      } catch {
        toast.info('Workspace saved. This browser could not cache the updated profile.');
      }
      qc.setQueryData(['me'], current => ({ ...(current || me), ...completed }));
      toast.success('Workspace ready');
    });
  };

  return (
    <AuthLayout wide eyebrow="Workspace setup">
      <ol className="sp-auth-steps" aria-label="Setup progress">
        {STEPS.map((label, i) => <li key={label} aria-current={step === i ? 'step' : undefined} data-complete={i < step || undefined}>
          <span aria-hidden="true">{i < step ? <Check size={14} /> : i + 1}</span>
          <span>{label}{i < step && <span className="sr-only"> complete</span>}</span>
        </li>)}
      </ol>
      {error && <div id="onboarding-error" className="sp-auth-notice" data-tone="error" role="alert">{error}</div>}
      {step === 0 && <section data-testid="onboarding-company" aria-labelledby="onboarding-title">
        <h2 id="onboarding-title">Welcome to Tether.</h2>
        <p className="sp-auth-description">Let's set up your workspace. First, what should we call it?</p>
        <form onSubmit={nextFromCompany} noValidate aria-busy={saving} aria-describedby={error ? 'onboarding-error' : undefined}>
          <Field label="Company name" id="onboarding-company-name">
            <input id="onboarding-company-name" name="company" data-testid="onboarding-company-input" value={company} onChange={e => setCompany(e.target.value)} placeholder="Acme Coaching" autoComplete="organization" autoFocus required maxLength={120} disabled={saving} />
          </Field>
          <fieldset className="sp-auth-funnels" aria-describedby="funnel-hint">
            <legend>Funnel type</legend>
            {FUNNEL_OPTIONS.map(o => <label key={o.value} className="sp-auth-choice" data-selected={funnel === o.value || undefined}>
              <input type="radio" name="funnel" value={o.value} checked={funnel === o.value} onChange={() => setFunnel(o.value)} disabled={saving} data-testid={`funnel-${o.value}`} required />
              <span><strong>{o.label}</strong><small>{o.desc}</small></span>
              {funnel === o.value && <Check size={15} aria-hidden="true" />}
            </label>)}
            <p id="funnel-hint">This tailors your dashboard. You can change it later in Settings.</p>
          </fieldset>
          <div className="sp-auth-actions sp-auth-actions-end">
            <button type="submit" className="sp-primary-button" data-testid="onboarding-continue" disabled={saving}>
              {saving ? <Loader2 size={15} className="sp-spin" aria-hidden="true" /> : <ArrowRight size={15} aria-hidden="true" />}{saving ? 'Saving…' : 'Continue'}
            </button>
          </div>
        </form>
      </section>}

      {step === 1 && <section data-testid="onboarding-domain" aria-labelledby="onboarding-title">
        <h2 id="onboarding-title">Custom tracking domain.</h2>
        <p className="sp-auth-description">Use a subdomain of your website for first-party tracking. Optional — you can set this up later in Settings.</p>
        {sysHost && <div className="sp-auth-notice" data-tone="success">Your default tracking domain is ready: <strong>{sysHost}</strong>. No DNS setup needed.</div>}
        <form onSubmit={e => { e.preventDefault(); nextFromDomain(false); }} aria-busy={saving}>
          <Field label="Tracking domain" id="onboarding-domain-name" hint="A subdomain of the site you run ads to, e.g. track.acme.com">
            <input id="onboarding-domain-name" name="domain" data-testid="onboarding-domain-input" value={domain} onChange={e => setDomain(e.target.value)} placeholder="track.yourdomain.com" aria-describedby="onboarding-domain-name-hint" autoComplete="off" autoCapitalize="none" spellCheck={false} disabled={saving} />
          </Field>
          <div className="sp-auth-dns">
            <h3>Add this DNS record at your domain provider</h3>
            <dl><div><dt>Type</dt><dd>CNAME</dd></div><div><dt>Host</dt><dd>{domainSub.split('.')[0] || 'track'}</dd></div><div><dt>Value</dt><dd>{cnameTarget}</dd></div></dl>
            <p>DNS and SSL can take a few minutes to go live. We verify the connection before using your custom domain.</p>
          </div>
          <div className="sp-auth-actions">
            <button type="button" className="sp-secondary-button" disabled={saving} onClick={() => { setError(''); setStep(0); }}><ArrowLeft size={15} aria-hidden="true" /> Back</button>
            <div><button type="button" className="sp-secondary-button" disabled={saving} onClick={() => nextFromDomain(true)}>Skip for now</button>
              <button type="submit" className="sp-primary-button" data-testid="onboarding-continue" disabled={saving}>{saving && <Loader2 size={15} className="sp-spin" aria-hidden="true" />}{verifying ? 'Verifying…' : saving ? 'Saving…' : 'Continue'}<ArrowRight size={15} aria-hidden="true" /></button>
            </div>
          </div>
        </form>
      </section>}

      {step === 2 && <section data-testid="onboarding-install" aria-labelledby="onboarding-title" aria-busy={saving}>
        <h2 id="onboarding-title">Install tracking.</h2>
        <p className="sp-auth-description">Add the script to every page of your funnel, then connect your sales webhooks.</p>
        {domain.trim() && !domainLive && <div className="sp-auth-notice"><strong>{domainSub}</strong> isn't verified yet. These URLs use your default domain. Verify in Settings once DNS is ready, then re-copy the script.</div>}
        {(!me?.site_key || !me?.api_key) && <div className="sp-auth-notice" data-tone="error" role="alert">
          <p>Tracking credentials are unavailable. Refresh workspace details before copying your installation code.</p>
          <button type="button" className="sp-secondary-button" onClick={refreshProfile} disabled={refreshing} aria-busy={refreshing}>{refreshing ? 'Refreshing…' : 'Refresh workspace details'}</button>
        </div>}
        <div className="sp-auth-install-fields">
          <Field label="Tracking script" hint="Paste into the <head> of your pages."><Mono label="tracking script" value={snippet}>{snippet}</Mono></Field>
          <Field label="API key" hint="Authenticates sale and registration webhooks. Keep it secret."><Mono label="API key" value={me?.api_key || ''}>{me?.api_key || '…'}</Mono></Field>
          <Field label="Sales webhook" hint="Point Stripe, Whop or GHL purchase events here."><Mono label="sales webhook" value={webhook}>POST {webhook}</Mono></Field>
        </div>
        <div className="sp-auth-actions">
          <button type="button" className="sp-secondary-button" disabled={saving} onClick={() => { setError(''); setStep(1); }}><ArrowLeft size={15} aria-hidden="true" /> Back</button>
          <button type="button" className="sp-primary-button" data-testid="onboarding-finish" disabled={saving} onClick={finish}>{saving ? <Loader2 size={15} className="sp-spin" aria-hidden="true" /> : <Check size={15} aria-hidden="true" />}{saving ? 'Saving…' : 'Go to dashboard'}</button>
        </div>
      </section>}
      <p className="sp-auth-footnote">Everything here can be changed later in Settings.</p>
    </AuthLayout>
  );
}
